import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';

import { ButtonV3 } from 'components/ComponentV2';
import { useResponsive } from 'hooks/useResponsive';
import { useCandidateProfile } from 'lib/contexts/candidate-profile-context';
import { ExperienceAttributes } from 'lib/models/candidate';
import { useAuth } from 'lib/providers/AuthProvider';

import { formatDate } from 'utils/format';
import EmptyBottomSection from '../EmptyBottomSection';
import FilledBottomSection from '../FilledBottomSection';
import AISuggestion from '../FilledBottomSection/AISuggestion';
import TopTitleBar from '../TopTitleBar';

import styles from '../Profile.common.module.scss';
import { AddExperienceModal } from './AddExperienceModal.component';
import { getExperienceInfo } from './utils';

export const Experience = ({ className }: { className?: string }) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedExperienceInfo, setSelectedExperienceInfo] = useState<
    (ExperienceAttributes & { isNew: boolean }) | null
  >(null);
  const { experienceComplete } = useCandidateProfile();
  const [t] = useTranslation('candidate.profile');
  const screens = useResponsive();

  const getExperienceDetails = (item: ExperienceAttributes) => {
    const startDateFormatted = item.start_date
      ? formatDate(new Date(item.start_date))
      : '';
    const endDateFormatted = item.presently_working
      ? t('experience.ongoing')
      : item.end_date
      ? formatDate(new Date(item.end_date))
      : '';

    return {
      title: `${item.role}, ${item.organisation_name}`,
      startDate: startDateFormatted,
      endDate: endDateFormatted,
      description: item.description || '',
      id: item.id || '',
    };
  };

  const { candidate } = useAuth();
  const [experienceInfo, setExperienceInfo] = useState<
    Array<ExperienceAttributes & { isNew: boolean }>
  >([]);

  useEffect(() => {
    if (candidate) {
      setExperienceInfo(getExperienceInfo(candidate));
    }
  }, [candidate]);
  const dataForExperience = experienceInfo.map(getExperienceDetails);

  const addExperience = useCallback(() => {
    setSelectedExperienceInfo(null);
    setModalOpen(true);
  }, []);

  const onEditClick = (id: string) => {
    const info = experienceInfo.find((item) => item.id === id);
    if (info) {
      setSelectedExperienceInfo(info);
      setModalOpen(true);
    }
  };

  return (
    <article className={className}>
      <TopTitleBar
        title={t('experience.title')}
        isInfoComplete={experienceComplete}
        iconName="icon_plus"
        onIconClick={addExperience}
      />
      {experienceComplete ? (
        <section className={styles.content}>
          {dataForExperience.map((item, index) => (
            <>
              <FilledBottomSection
                key={index}
                id={item.id}
                title={item.title}
                startDate={item.startDate}
                endDate={item.endDate}
                isLast={dataForExperience.length - 1 === index}
                description={item.description}
                onEditClick={() => onEditClick(item.id)}
              />
              {!screens.sm_desktop_down && (
                <AISuggestion onIconClick={() => onEditClick(item.id)} />
              )}
            </>
          ))}

          <ButtonV3
            onClick={addExperience}
            startIcon="icon_plus"
            size="small"
            color="secondary"
          >
            {t('experience.button-text')}{' '}
          </ButtonV3>
        </section>
      ) : (
        <EmptyBottomSection
          desc={t('experience.desc')}
          addButtonText={t('experience.button-text')}
          createWithAIButton={true}
          onAddIconClick={addExperience}
          onAIButtonClick={addExperience}
        />
      )}
      {isModalOpen ? (
        <AddExperienceModal
          isOpen={isModalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedExperienceInfo(null);
          }}
          isEdit={selectedExperienceInfo === null ? false : true}
          selectedExperienceInfo={selectedExperienceInfo}
        />
      ) : null}
    </article>
  );
};
